import React, { useMemo, useState } from 'react';
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';
import { filterExtensionsBySearchableInfo } from 'flow/utils/extension-utils';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import AssigneeItem from './AssigneeItem';
import { UNASSIGNED_ASSIGNEE_VALUE } from 'flow/types/Tickets';
import { useFilterPopupHeight } from './useFilterPopupHeight';

type AssigneeOption = ExtensionKey | typeof UNASSIGNED_ASSIGNEE_VALUE;

interface Props {
    value: AssigneeOption | undefined;
    onChange: (next: ExtensionKey | undefined) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const displayName = (extension: any): string => {
    const contact = extension?.get?.('contact');
    if (!contact) return '';
    const first = contact.get('firstName') ?? '';
    const last = contact.get('lastName') ?? '';
    return `${first} ${last}`.trim();
};

const UnassignedOption = ({ label }: { label: string }) => (
    <>
        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500 dark:bg-gray-100">
            <Icon iconKey="person" size="1rem" />
        </div>
        <span className="truncate">{label}</span>
    </>
);

const AssigneePicker = ({ value, onChange }: Props) => {
    const { i18next } = useAppContext();
    const extensions = useAppSelector(selectExtensions);
    const [query, setQuery] = useState('');

    const { wrapperRef, popupHeight } = useFilterPopupHeight();

    const filteredExtensionKeys = useMemo<ExtensionKey[]>(() => {
        const list = query
            ? filterExtensionsBySearchableInfo(extensions, query)
            : extensions;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return list.toArray().map((ext: any) => ext.get('key'));
    }, [extensions, query]);

    const unassignedLabel = i18next.t('tickets.filter.unassigned');
    const showUnassignedOption =
        !query ||
        unassignedLabel.toLowerCase().includes(query.trim().toLowerCase());

    const virtualOptions = useMemo<AssigneeOption[]>(
        () =>
            showUnassignedOption
                ? [UNASSIGNED_ASSIGNEE_VALUE, ...filteredExtensionKeys]
                : filteredExtensionKeys,
        [showUnassignedOption, filteredExtensionKeys],
    );

    return (
        <Combobox
            value={value ?? null}
            onChange={(next: ExtensionKey | null) => {
                onChange(next ?? undefined);
                setQuery('');
            }}
            onClose={() => setQuery('')}
            immediate={true}
            virtual={
                virtualOptions.length > 0
                    ? { options: virtualOptions }
                    : undefined
            }
        >
            <div ref={wrapperRef} className="relative">
                <ComboboxInput
                    className="w-full rounded-sm bg-gray-200 py-2 ps-3 pe-16 text-sm text-gray-800 placeholder:text-gray-500 focus:outline-hidden dark:bg-gray-100"
                    autoComplete="off"
                    placeholder={i18next.t(
                        'tickets.filter.assignee_placeholder',
                    )}
                    displayValue={(key: AssigneeOption | null) => {
                        if (!key) return '';
                        if (key === UNASSIGNED_ASSIGNEE_VALUE) {
                            return unassignedLabel;
                        }
                        return displayName(extensions.get(key));
                    }}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {value && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange(undefined);
                            setQuery('');
                        }}
                        className="hover:darken-10 absolute end-9 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-gray-600"
                        aria-label={i18next.t('Generics.Clear')}
                    >
                        <Icon iconKey="cancel" size="1rem" />
                    </button>
                )}
                <ComboboxButton className="hover:darken-10 absolute end-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-gray-600">
                    <Icon iconKey="arrow_down" size="1.5rem" />
                </ComboboxButton>
                <ComboboxOptions
                    anchor="bottom start"
                    modal={false}
                    style={
                        popupHeight
                            ? ({
                                  '--anchor-max-height': `${popupHeight}px`,
                              } as React.CSSProperties)
                            : undefined
                    }
                    className="scrollbar shadow-base scrollbar-gutter-stable z-50 mt-1 w-(--input-width) origin-top overflow-auto rounded-lg bg-white py-2 ps-2 outline-gray-100 focus:outline dark:bg-gray-300"
                >
                    {virtualOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            {i18next.t('tickets.filter.no_assignees')}
                        </div>
                    ) : (
                        // Render-function children — Headless UI's virtual
                        // mode calls this once per visible row, mounting
                        // only those that fit in the dropdown viewport.
                        ({ option }) => (
                            <ComboboxOption
                                value={option}
                                className={({ focus }) =>
                                    twMerge(
                                        'flex w-full cursor-pointer items-center gap-x-2 rounded-sm py-1.5 ps-2 text-sm text-gray-800',
                                        focus && 'bg-gray-200 dark:bg-gray-400',
                                    )
                                }
                            >
                                {option === UNASSIGNED_ASSIGNEE_VALUE ? (
                                    <UnassignedOption label={unassignedLabel} />
                                ) : (
                                    <AssigneeItem
                                        assigneeExtensionKey={option}
                                    />
                                )}
                            </ComboboxOption>
                        )
                    )}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
};

export default AssigneePicker;
